<script setup>
import { useForm } from "@/form";
import { PrimaryButton, SecondaryButton } from "@Components/Button";
import { useI18n } from "vue-i18n";
import Loading from "@Components/Animation/Loading.vue";
import { MagicIcon } from "@Icons/index";
import { useRoute } from "vue-router";
import _cloneDeep from "lodash/cloneDeep";
import { getCurrentInstance, provide, ref } from "vue";
import Display from "./Partials/Display.vue";
import LanguageMenu from "@Components/LanguageMenu.vue";
import { CircleXIcon, EyeIcon, PencilIcon } from "lucide-vue-next";
import SettingsPart from "./Partials/FormParts/SettingsPart.vue";
import PublishModal from "./Partials/Modals/PublishModal.vue";
import Layout from "./Partials/Layout.vue";
import TitlePart from "./Partials/FormParts/TitlePart.vue";
import Logs from "./Partials/Logs.vue";
import Tooltip from "@Components/Tooltip.vue";
import { useStore } from "@/Stores/singleModeGenerations";
import SaveTemplateModal from "./Partials/Modals/SaveTemplateModal.vue";

const { t: __ } = useI18n();

const route = useRoute();
const store = useStore();

const focusedElement = ref();
const publishModal = ref();
const saveTemplateModal = ref();

const fetchGeneration = async () => {
    return await useForm({ id: route.params.id }).post("single_mode_show");
};

const refreshGeneration = async () => {
    generation.value = await fetchGeneration();

    store.update(generation.value.id, generation.value);

    if (generation.value.status === "idle" || generation.value.status === "pending") {
        setTimeout(() => {
            refreshGeneration();
        }, 1500);
    }
};

const [generation, personas, users, templates, statuses, categories, postTypes] = await Promise.all([
    new Promise((resolve, reject) => {
        fetchGeneration()
            .then((response) => {
                resolve(ref(response));
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("personas_index")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_users")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_templates")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_statuses")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_categories")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_post_types")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
]);

const submitForm = async () => {
    const response = await form.post("single_mode_update");

    store.update(generation.value.id, response);

    refreshGeneration();
};

const form = useForm({ ..._cloneDeep(generation.value), regenerate: false });
if (form.language) {
    form.language = form.language.key;
}

provide("updateGeneration", (value) => {
    generation.value = value;
});

refreshGeneration();
</script>

<template>
    <Layout>
        <div class="overflow-hidden grid flex-1 grid-cols-12">
            <div class="overflow-y-auto col-span-full px-4 py-8 lg:col-span-6 lg:pr-4 lg:pl-8 sm:px-6">
                <form @submit.prevent="submitForm" class="space-y-6">
                    <LanguageMenu v-model="form.language" />

                    <TitlePart v-model="form" />

                    <SettingsPart v-model="form" :profiles="personas" />

                    <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center lg:space-x-3">
                        <SecondaryButton @click.prevent="$refs.saveTemplateModal.show()" class="justify-center">
                            {{ __("Enregister le template") }}
                        </SecondaryButton>
                        <div class="flex justify-between items-center mt-3 space-x-3 lg:justify-end lg:mt-0">
                            <SecondaryButton
                                @click="form.regenerate = false"
                                type="submit"
                                :processing="form.processing && !form.regenerate"
                                :success="form.recentlySuccessful && !form.regenerate"
                                :disabled="['idle', 'pending'].includes(generation.status)">
                                {{ __("Sauvegarder") }}
                            </SecondaryButton>
                            <PrimaryButton
                                @click="form.regenerate = true"
                                type="submit"
                                :processing="form.processing && form.regenerate"
                                :success="form.recentlySuccessful && form.regenerate"
                                :icon="MagicIcon"
                                :disabled="['idle', 'pending'].includes(generation.status)">
                                {{ __("Sauvegarder et regénérer") }}
                            </PrimaryButton>
                        </div>
                    </div>
                </form>
            </div>
            <div class="overflow-y-auto col-span-full px-4 py-8 lg:col-span-6 lg:pr-4 lg:pl-8 sm:px-6">
                <template v-if="generation.status === 'success'">
                    <div class="flex justify-end items-center p-4 mb-6 space-x-3 bg-gray-50 rounded-lg dark:bg-gray-900">
                        <Tooltip v-if="generation.post_id" :content="__('Voir le post')">
                            <a
                                :href="generation.display_link"
                                target="_blank"
                                class="flex items-center text-gray-600 rounded-full dark:text-gray-400 dark:focus-visible:outline-gray-700 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                                <EyeIcon class="w-5 h-5" aria-hidden="true" />
                            </a>
                        </Tooltip>
                        <Tooltip v-if="generation.post_id" :content="__('Modifier le post')">
                            <a
                                :href="generation.edit_link"
                                target="_blank"
                                class="flex items-center text-gray-600 rounded-full dark:text-gray-400 dark:focus-visible:outline-gray-700 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                                <PencilIcon class="w-5 h-5" aria-hidden="true" />
                            </a>
                        </Tooltip>
                        <PrimaryButton @click="$refs.publishModal.show()" :z="1">{{ generation.post_id ? __("Republier") : __("Publier") }}</PrimaryButton>
                    </div>

                    <Display :generation="generation" :profiles="personas" />

                    <PublishModal
                        ref="publishModal"
                        :generation="generation"
                        :templates="templates"
                        :statuses="statuses"
                        :categories="categories"
                        :postTypes="postTypes"
                        :users="users"
                        @refresh="refreshGeneration" />
                </template>
                <div v-else-if="generation.status === 'idle' && generation.is_not_started" class="min-h-96 flex flex-col justify-center items-center">
                    <CircleXIcon class="w-16 h-16 text-red-500" />
                    <div class="flex flex-col items-center mt-6 space-y-3">
                        <div class="max-w-md text-sm text-center text-gray-600 dark:text-gray-400">
                            {{ __("La génération n'a pas commencé depuis plus de 5 minutes. Il peut s'agir d'un problème de tâche CRON WordPress.") }}
                        </div>
                        <div class="space-y-2">
                            <div class="text-sm text-gray-600 dark:text-gray-400">{{ __("Étapes de vérification :") }}</div>
                            <ol class="space-y-1 text-sm list-inside list-decimal text-gray-600 dark:text-gray-400">
                                <li
                                    v-html="
                                        __('Installez le plugin {startlink}WP Crontrol{endlink}.', {
                                            startlink:
                                                '<a href=\'https://wordpress.org/plugins/wp-crontrol/\' target=\'_blank\' class=\'font-bold text-gray-900 dark:text-white hover:underline\'>',
                                            endlink: '</a>',
                                        })
                                    "></li>
                                <li>
                                    {{ __("Rendez-vous sur la page") }}
                                    <a
                                        class="font-bold text-gray-900 dark:text-white hover:underline"
                                        :href="getCurrentInstance().appContext.config.globalProperties.$admin('tools.php?page=wp-crontrol')"
                                        target="_blank"
                                        >{{ __("Outils / Événements Cron") }}.</a
                                    >
                                </li>
                                <li>
                                    {{ __("Vérifiez que la tâche 'action_scheduler_run_queue' est bien présente et n'est pas en erreur.") }}
                                </li>
                                <li>
                                    {{ __("Rendez-vous sur la page") }}
                                    <a
                                        class="font-bold text-gray-900 dark:text-white hover:underline"
                                        :href="getCurrentInstance().appContext.config.globalProperties.$admin('tools.php?page=action-scheduler')"
                                        target="_blank"
                                        >{{ __("Outils / Scheduled Actions") }}.</a
                                    >
                                </li>
                                <li>
                                    {{
                                        __(
                                            "Si une alerte indique qu'une migration est en cours, attendez que la migration soit terminée ou forcez la migration en cliquant sur le bouton 'RUN' de la tâche correspondante dans l'onglet 'Pending'."
                                        )
                                    }}
                                </li>
                                <li>
                                    {{ __("Vérifiez s'il y a des tâches récentes échouées dans l'onglet 'Failed'.") }}
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
                <div v-else-if="generation.status === 'idle' || generation.status === 'pending'" class="min-h-96 flex flex-col justify-center items-center">
                    <Loading class="w-24 h-24" />
                    <div class="mt-6 text-sm text-gray-600 dark:text-gray-400">
                        {{ __("Génération en cours...") }}
                    </div>
                </div>
                <div v-else class="min-h-96 flex flex-col justify-center items-center">
                    <CircleXIcon class="w-20 h-20 text-gray-400 dark:text-gray-600" />
                    <div class="mt-6 text-sm text-gray-600 dark:text-gray-400">
                        {{ __("Erreur lors de la génération de l'article") }}
                    </div>
                    <Logs :generation="generation" class="mt-6" />
                </div>
            </div>

            <SaveTemplateModal ref="saveTemplateModal" :generation="form" />
        </div>
    </Layout>
</template>
